import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, TrendingUp, Zap, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('username', data.username);
      toast.success('Login successful!');
      onLoginSuccess();
    } catch (error) {
      toast.error('Login failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-lines relative overflow-hidden flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-neon-purple/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8 animate-slide-in">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-card border-2 border-neon-cyan neon-border-cyan mb-4">
            <TrendingUp className="w-10 h-10 text-neon-cyan" />
          </div>
          <h1 className="text-4xl font-bold mb-2 neon-glow-cyan tracking-wider">
            PKAI TRADING
          </h1>
          <p className="text-muted-foreground text-sm uppercase tracking-widest">
            Automated Intelligence • Real-Time Execution
          </p>
        </div>

        {/* Login Card */}
        <div className="glass-panel rounded-lg p-8 animate-slide-in" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-neon-cyan/30">
            <Shield className="w-5 h-5 text-neon-cyan" />
            <span className="text-sm uppercase tracking-wider text-foreground/80">
              Secure Access
            </span>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-widest text-muted-foreground">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                className="h-12 bg-background/50 border-neon-cyan/30 text-foreground placeholder:text-muted-foreground focus:border-neon-cyan focus:ring-neon-cyan font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 bg-background/50 border-neon-cyan/30 text-foreground placeholder:text-muted-foreground focus:border-neon-cyan focus:ring-neon-cyan font-mono"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-neon-cyan hover:bg-neon-cyan/90 text-background font-bold uppercase tracking-widest neon-border-cyan transition-all"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4 animate-pulse" />
                  Authenticating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Access System
                </span>
              )}
            </Button>
          </form>

          {/* COMBO PLACEMENT 1: Login Screen Disclaimer */}
          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-yellow-500 text-xl flex-shrink-0">⚠️</span>
              <div className="text-sm">
                <p className="text-yellow-500 font-bold mb-1">SWING TRADING ONLY</p>
                <p className="text-foreground/90">
                  This bot is for active swing trading (5-30 days), NOT retirement accounts or long-term investing. Trade with risk capital only.
                </p>
              </div>
            </div>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-6 border-t border-neon-cyan/20 grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-neon-cyan font-bold text-lg font-mono">AI</div>
              <div className="text-xs text-muted-foreground uppercase">Powered</div>
            </div>
            <div>
              <div className="text-neon-purple font-bold text-lg font-mono">24/7</div>
              <div className="text-xs text-muted-foreground uppercase">Active</div>
            </div>
            <div>
              <div className="text-success font-bold text-lg font-mono">AUTO</div>
              <div className="text-xs text-muted-foreground uppercase">Trading</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-muted-foreground uppercase tracking-wider">
          <p>Protected by Advanced Encryption</p>
        </div>
      </div>
    </div>
  );
}
